import {
  createUnifiedTheme,
  palettes,
  type PageTheme,
  type SupportedThemes,
  type SupportedVersions,
  type UnifiedTheme,
  type UnifiedThemeOptions,
} from '@backstage/theme';
import {
  brand,
  darkMuiOnly,
  darkTokens,
  fontFamily,
  lightMuiOnly,
  lightTokens,
  radius,
  sidebarPillInset,
  type ModeTokens,
  type MuiOnlyTokens,
} from '@internal/plugin-waadoo-core';

/*
 * The Material UI theme, aligned on the Backstage UI tokens of
 * components/ui/tokens.ts (the same values styles.css gives BUI). It styles every
 * upstream page built on Material UI or core-components — catalog tables,
 * search, techdocs, scaffolder forms, settings — and the sidebar.
 *
 * createUnifiedTheme builds a Material UI v5 theme from these options and
 * derives the v4 one from it (transformV5ComponentThemesToV4), so the
 * `components` overrides below — written with v5 names — reach the v4
 * components the upstream plugins still render, and the `Backstage*` entries
 * reach the core-components makeStyles sheets of the same name.
 */

type Mode = 'light' | 'dark';

/**
 * Page headers: no coloured band. The Material UI header (core-components
 * `Header`) is drawn on the app background in ink, like the Backstage UI
 * `Header` of our own pages (the Odoo branch page), so the two kinds of page
 * read as one app.
 *
 * One page theme for every page. A page picks its theme by id, and the catalog
 * entity page uses the entity's `spec.type` as that id (`erp`, `service`,
 * `website`… — plugin-catalog 2.0.1 alpha EntityLayout), so a header look
 * reserved to entity pages cannot be expressed here: the ids are open-ended
 * and shared with other pages. Any id missing from this record falls back to
 * `defaultPageTheme` (`getPageTheme` in @backstage/theme 0.7.3
 * createBaseThemeOptions), hence the single `home` entry.
 *
 * `backgroundImage: 'none'` leaves the header transparent over the page.
 * `fontColor` is what the header's title, type line, labels and context-menu
 * button are painted with.
 *
 * A few non-header parts read page themes too, and follow: the item card
 * header (`card` id, and a scaffolder template card's `spec.type`) gets its
 * surface from BackstageItemCardHeader below; plugin-org's ownership tiles
 * (id = entity type or kind) become ink on paper; the scaffolder's ongoing-task
 * menu button (`website`) is ink, as it sits on a header. A tinted look for
 * those through page themes alone is not possible: their ids are the same
 * open-ended entity types the entity page header uses.
 */
function pageThemes(t: ModeTokens): Record<string, PageTheme> {
  return {
    home: {
      colors: [t['--bui-bg-app']],
      shape: 'none',
      backgroundImage: 'none',
      fontColor: t['--bui-fg-primary'],
    },
  };
}

function palette(mode: Mode, t: ModeTokens) {
  const base = mode === 'light' ? palettes.light : palettes.dark;

  return {
    ...base,

    // Interactive accent. `dark` is what Material UI paints a hovered
    // contained button with, so it is the hover step of either mode — darker
    // in light mode, lighter in dark mode.
    primary: {
      main: t['--bui-bg-solid'],
      dark: t['--bui-bg-solid-hover'],
      contrastText: t['--bui-fg-solid'],
    },
    // Brand orange, under ink: white on it is only 3.37:1.
    secondary: {
      main: brand.orange,
      contrastText: lightTokens['--bui-fg-primary'],
    },
    error: { main: t['--bui-fg-danger'] },
    warning: { main: t['--bui-fg-warning'] },
    success: { main: t['--bui-fg-success'] },
    info: { main: t['--bui-fg-info'] },

    background: {
      default: t['--bui-bg-app'],
      paper: t['--bui-bg-neutral-1'],
    },
    text: {
      primary: t['--bui-fg-primary'],
      secondary: t['--bui-fg-secondary'],
      disabled: t['--bui-fg-disabled'],
    },
    divider: t['--bui-border-1'],

    // Backstage additions to the palette.
    border: t['--bui-border-1'],
    textContrast: t['--bui-fg-primary'],
    textSubtle: t['--bui-fg-secondary'],
    textVerySubtle: t['--bui-border-2'],
    link: t['--waadoo-link'],
    linkHover: t['--waadoo-link-hover'],
    errorBackground: t['--bui-bg-danger'],
    errorText: t['--bui-fg-danger-on-bg'],
    warningBackground: t['--bui-bg-warning'],
    warningText: t['--bui-fg-warning-on-bg'],
    infoBackground: t['--bui-bg-info'],
    infoText: t['--bui-fg-info-on-bg'],
    status: {
      ...base.status,
      ok: t['--bui-fg-success'],
      warning: t['--bui-fg-warning'],
      error: t['--bui-fg-danger'],
      running: t['--bui-fg-info'],
      aborted: t['--bui-fg-secondary'],
    },
    // Banners are solid colours under white text in both modes, so both
    // modes take the light-mode state colours (5.87:1 or more under white).
    banner: {
      ...base.banner,
      info: lightTokens['--bui-fg-info'],
      error: lightTokens['--bui-fg-danger'],
      warning: lightTokens['--bui-fg-warning'],
    },
    navigation: {
      background: t['--bui-bg-neutral-1'],
      indicator: brand.orange,
      color: t['--bui-fg-secondary'],
      selectedColor: t['--waadoo-link'],
      navItem: { hoverBackground: t['--bui-bg-neutral-2'] },
      submenu: { background: t['--bui-bg-neutral-2'] },
    },
    pinSidebarButton: {
      icon: t['--bui-fg-primary'],
      background: t['--bui-bg-neutral-3'],
    },
    tabbar: { indicator: brand.orange },
  };
}

function components(
  t: ModeTokens,
  mui: MuiOnlyTokens,
): UnifiedThemeOptions['components'] {
  // createUnifiedTheme merges these over @backstage/theme's
  // defaultComponentThemes one component at a time, not one rule at a time:
  // an entry here replaces the default entry of the same name entirely. The
  // default rules still wanted are therefore restated, marked "(default)".
  return {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'unset' }, // (default)
        rounded: { borderRadius: radius.card },
        // Cards, tables, accordions: a 1px ring drawn as a shadow, so the
        // box keeps its size, plus the soft card shadow.
        elevation1: {
          boxShadow: `0 0 0 1px ${t['--bui-border-1']}, ${t['--waadoo-shadow-card']}`,
        },
        // Menus and popovers (elevation 8), dialogs (24): the overlay shadow.
        elevation8: { boxShadow: t['--bui-shadow'] },
        elevation24: { boxShadow: t['--bui-shadow'] },
      },
    },
    MuiCard: {
      styleOverrides: {
        // (default) actions at the bottom when cards are stretched in a grid.
        root: { display: 'flex', flexDirection: 'column' },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { border: `1px solid ${t['--bui-border-1']}` },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.dialog },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // The radius of BUI's action buttons (« P3 », styles.css).
          borderRadius: radius.button,
          textTransform: 'none',
          fontWeight: 600,
        },
        // (default) text buttons keep the padding of the other variants.
        text: { padding: undefined },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: radius.control },
      },
    },
    MuiChip: {
      // Chips are pills already (radius = half their height); only the
      // neutral surface changes. Every other rule is the default one.
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: t['--bui-bg-neutral-3'],
          marginRight: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }),
        label: ({ theme }) => ({
          lineHeight: theme.spacing(2.5),
          fontWeight: theme.typography.fontWeightMedium,
          fontSize: theme.spacing(1.75),
        }),
        labelSmall: ({ theme }) => ({
          fontSize: theme.spacing(1.5),
        }),
        deleteIcon: ({ theme }) => ({
          width: theme.spacing(3),
          height: theme.spacing(3),
          margin: theme.spacing(0, 0.75, 0, -0.75),
        }),
        deleteIconSmall: ({ theme }) => ({
          width: theme.spacing(2),
          height: theme.spacing(2),
          margin: theme.spacing(0, 0.5, 0, -0.5),
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // (default) dense cells.
        root: ({ theme }) => ({
          wordBreak: 'break-word',
          overflow: 'hidden',
          verticalAlign: 'middle',
          lineHeight: '1',
          margin: 0,
          padding: theme.spacing(3, 2, 3, 2.5),
          borderBottom: 0,
        }),
        sizeSmall: ({ theme }) => ({
          padding: theme.spacing(1.5, 2, 1.5, 2.5),
        }),
        // Smaller, secondary, on a subtle surface.
        head: {
          wordBreak: 'break-word',
          overflow: 'hidden',
          lineHeight: '1',
          color: t['--bui-fg-secondary'],
          backgroundColor: t['--bui-bg-neutral-2'],
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 24 }, // (default)
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: brand.orange,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 24,
          textTransform: 'none',
          letterSpacing: 0,
          fontWeight: 600,
          borderRadius: `${radius.control}px ${radius.control}px 0 0`,
          '&:hover': {
            color: t['--bui-fg-primary'],
            backgroundColor: t['--bui-bg-neutral-1-hover'],
          },
          [theme.breakpoints.up('md')]: {
            minWidth: 120,
            fontSize: theme.typography.pxToRem(14),
          },
        }),
        textColorPrimary: { color: t['--bui-fg-secondary'] },
      },
    },
    MuiTypography: {
      styleOverrides: {
        // The `button` variant is uppercase in Material UI's defaults. The
        // catalog's entity tabs print their label with it (plugin-catalog
        // 2.0.1 alpha EntityTabsGroup), past the MuiTab and
        // BackstageHeaderTabs rules that already drop the uppercase.
        button: { textTransform: 'none' },
      },
    },
    MuiTooltip: {
      // Same look as BUI's tooltip: a surface with a border, not a dark slab.
      styleOverrides: {
        tooltip: {
          backgroundColor: t['--bui-bg-app'],
          color: t['--bui-fg-primary'],
          border: `1px solid ${t['--bui-border-1']}`,
          borderRadius: radius.control,
          boxShadow: t['--bui-shadow'],
          fontSize: '0.75rem',
          padding: '6px 10px',
        },
        arrow: { color: t['--bui-bg-app'] },
      },
    },

    // core-components (makeStyles sheets named Backstage*).
    // The page header, on the app background (see pageThemes): the sizes and
    // inks of the Backstage UI Header — 24px semibold title, secondary
    // metadata — and no shadow. One hairline closes the header block: under
    // the header itself on a page without tabs, under the tab bar otherwise.
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }) => ({
          boxShadow: 'none',
          padding: theme.spacing(3, 3, 2),
          borderBottom: `1px solid ${t['--bui-border-1']}`,
          // A tab bar follows (core-components HeaderTabs, the catalog's
          // entity tabs: a wrapper whose first child is Material UI's Tabs
          // root): it draws the line, the header does not. `Mui*` class names
          // stay readable in production builds, suffixed `-N` under a nested
          // theme, hence the substring match. A browser without `:has()`
          // drops this rule and shows both lines.
          '&:has(+ * > [class*="MuiTabs-root"])': { borderBottom: 'none' },
        }),
        title: {
          fontSize: '1.5rem',
          fontWeight: 600,
          lineHeight: 1.4,
        },
        subtitle: {
          color: t['--bui-fg-secondary'],
          opacity: 1,
        },
        // The kind line above the title ("component — erp" on an entity):
        // sentence case instead of capitals.
        type: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: t['--bui-fg-secondary'],
          opacity: 1,
          '&::first-letter': { textTransform: 'uppercase' },
        },
      },
    },
    // The labels on the right of a header (owner, lifecycle on an entity):
    // a small secondary caption over an ink value.
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          color: t['--bui-fg-secondary'],
          fontSize: '0.75rem',
          fontWeight: 600,
        },
        value: {
          color: t['--bui-fg-primary'],
          fontSize: '0.875rem',
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        // On the app background, like the header above: the tabs and the
        // header read as one block, closed by this line.
        tabsWrapper: {
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${t['--bui-border-1']}`,
        },
        defaultTab: {
          textTransform: 'none',
          letterSpacing: 0,
          fontSize: '0.875rem',
          // These tab bars use `textColor="inherit"`, which Material UI dims
          // to 0.7 when unselected (`textColorInherit`): the secondary ink
          // would fall to 2.98:1 on the app background. Selected tabs are
          // told apart by colour (`selected`) and the brand indicator. Same
          // specificity as `textColorInherit`, and this sheet is created
          // after the tab's (HeaderTabs and EntityTabsList import their tab
          // first), so it wins; the disabled tab's 0.5, nested, still holds.
          // Scoped here because Material UI's Tabs default to `inherit`:
          // a global MuiTab rule would undim every other tab bar too.
          opacity: 1,
        },
        // The default hover paints the app background, invisible once the
        // tabs sit on it.
        tabRoot: {
          '&:hover': {
            backgroundColor: t['--bui-bg-neutral-2-hover'],
            color: t['--bui-fg-primary'],
          },
        },
      },
    },
    // The About card's row of icon links ("Voir le code source"…), the only
    // user of IconLinkVertical in the installed plugins: labels in sentence
    // case instead of spaced capitals.
    BackstageIconLinkVertical: {
      styleOverrides: {
        label: {
          textTransform: 'none',
          letterSpacing: 0,
        },
      },
    },
    // The filled favourite star: amber instead of core-components' #f3ba37,
    // which is 1.63:1 on the light app background the entity header now has.
    BackstageFavoriteToggleIcon: {
      styleOverrides: {
        icon: { color: t['--bui-fg-warning'] },
      },
    },
    // Header strip of the item cards (core-components ItemCardHeader: TechDocs
    // documentation cards, scaffolder template cards). It used to take the
    // `card` page theme, or the template's type, as a coloured band; with
    // one neutral page theme (see pageThemes) it would be bare paper. A
    // neutral-2 strip with ink text keeps it a header. A colour, not an
    // image: the template card sets `backgroundImage` on top of this rule,
    // which does not touch the colour.
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundColor: t['--bui-bg-neutral-2'],
          color: t['--bui-fg-primary'],
          borderBottom: `1px solid ${t['--bui-border-1']}`,
        },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          textTransform: 'none',
          fontSize: '0.75rem',
          color: t['--bui-fg-secondary'],
          backgroundColor: t['--bui-bg-neutral-2'],
          borderTop: 'none',
          borderBottom: `1px solid ${t['--bui-border-1']}`,
        },
      },
    },
    // Only `drawer`, never `drawerOpen`: core-components computes it from
    // the sidebar config (the open width), as a function rule, and an
    // override of a function rule replaces it instead of adding to it — the
    // open drawer then kept the closed 72px width (2026-10-17, compact
    // menu). `BackstageSidebarPage.root` below is an object rule, merged.
    BackstageSidebar: {
      styleOverrides: {
        drawer: { borderRight: `1px solid ${t['--bui-border-1']}` },
      },
    },
    // On a phone, the page's room under the bottom bar: core-components pads
    // it by the bar's height (`mobileSidebarHeight`, 56px, under
    // `breakpoints.down('xs')`); the bar also takes the home indicator's
    // safe area (styles.css, the phone rules), added here. Material UI
    // deep-merges an object override into the rule (getStylesCreator), and
    // this key is the one `down('xs')` gives, so only that padding is
    // replaced: the computed `paddingLeft` of larger screens stays.
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          '@media (max-width:599.95px)': {
            paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          },
        },
      },
    },
    // The active item becomes a tinted pill instead of a bar on the left
    // edge. The pill is a pseudo-element inset in the row, so the geometry
    // core-components computes for open and closed rows is untouched.
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          position: 'relative',
          // Own stacking context: the pill (z-index -1) paints above the
          // drawer background and below the icon and label.
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: `${sidebarPillInset.block}px ${sidebarPillInset.inline}px`,
            borderRadius: radius.pill,
            backgroundColor: 'transparent',
            transition: 'background-color 150ms ease',
            zIndex: -1,
          },
        },
        highlightable: {
          '&:hover': {
            background: 'none',
            '&::before': { backgroundColor: t['--bui-bg-neutral-2'] },
          },
        },
        // Merged into core-components' own `selected` rule, which draws the
        // left bar and shifts the icon by its width; both are undone here.
        selected: {
          '&$root': {
            borderLeft: 'none',
            color: t['--waadoo-link'],
            '&::before': { backgroundColor: mui.navSelectedBackground },
          },
          '& $closedItemIcon': { paddingRight: 0 },
          '& $iconContainer': { marginLeft: 0 },
        },
      },
    },
  };
}

/**
 * createUnifiedTheme (@backstage/theme 0.7.3) forwards palette, typography and
 * page themes only — UnifiedThemeOptions has no `shape` — so the base radius
 * is set on the two built themes. Built once: UnifiedThemeProvider calls
 * getTheme on every render, and a new object each time would make Material UI
 * restyle the whole tree on each of them.
 */
function withBorderRadius(
  theme: UnifiedTheme,
  borderRadius: number,
): UnifiedTheme {
  const themes = new Map<SupportedVersions, SupportedThemes>();
  for (const version of ['v4', 'v5'] as const) {
    const built = theme.getTheme(version);
    if (built) {
      themes.set(version, {
        ...built,
        shape: { ...built.shape, borderRadius },
      });
    }
  }
  return { getTheme: version => themes.get(version) };
}

function createWaadooTheme(mode: Mode): UnifiedTheme {
  const t = mode === 'light' ? lightTokens : darkTokens;
  const mui = mode === 'light' ? lightMuiOnly : darkMuiOnly;

  return withBorderRadius(
    createUnifiedTheme({
      palette: palette(mode, t),
      fontFamily,
      defaultPageTheme: 'home',
      pageTheme: pageThemes(t),
      components: components(t, mui),
    }),
    radius.card,
  );
}

export const lightTheme = createWaadooTheme('light');
export const darkTheme = createWaadooTheme('dark');
